import { AccountType } from "@common/constants/domains/accounting/accounts";
import {
    ChartOfAccountsTemplateUploadResponse,
    ChartOfAccountsTemplateUploadRow,
} from "@common/schemas/chart-of-accounts-template-upload.schema";
import { importedAccountGroupSubtreeIds } from "@common/helpers/asIsTypeReviewInheritance";

export type TypedChartOfAccountsTemplateUploadRow = Omit<
    ChartOfAccountsTemplateUploadRow,
    "kickType"
> & { kickType: AccountType };

export interface TypedChartOfAccountsTemplateUpload {
    groups: TypedChartOfAccountsTemplateUploadRow[];
    accounts: TypedChartOfAccountsTemplateUploadRow[];
}

export interface TypeMappingRow {
    id: string;
    kind: "group" | "account";
    name: string;
    codeText: string | null;
    isTopLevel: boolean;
    type: AccountType | null;
    children: TypeMappingRow[];
}

interface TypeMappingChild {
    row: ChartOfAccountsTemplateUploadRow;
    kind: TypeMappingRow["kind"];
}

interface SubtreeAccountsParams {
    chart: ChartOfAccountsTemplateUploadResponse;
    rootGroupId: string;
}

function subtreeAccounts({
    chart,
    rootGroupId,
}: SubtreeAccountsParams): ChartOfAccountsTemplateUploadRow[] {
    const groupIds = importedAccountGroupSubtreeIds(rootGroupId, chart.groups);
    return chart.accounts.filter(
        ({ parentGroupId }) =>
            parentGroupId != null && groupIds.has(parentGroupId),
    );
}

interface ApplyTopLevelTypeParams {
    chart: ChartOfAccountsTemplateUploadResponse;
    rowId: string;
    type: AccountType;
}

export function applyTopLevelType({
    chart,
    rowId,
    type,
}: ApplyTopLevelTypeParams): ChartOfAccountsTemplateUploadResponse {
    const isRootGroup = chart.groups.some(
        ({ id, parentGroupId }) => id === rowId && parentGroupId == null,
    );
    if (!isRootGroup) {
        return {
            groups: chart.groups,
            accounts: chart.accounts.map((account) =>
                account.id === rowId && account.parentGroupId == null
                    ? { ...account, kickType: type }
                    : account,
            ),
        };
    }
    const groupIds = importedAccountGroupSubtreeIds(rowId, chart.groups);
    return {
        groups: chart.groups.map((group) =>
            groupIds.has(group.id) ? { ...group, kickType: type } : group,
        ),
        accounts: chart.accounts.map((account) =>
            account.parentGroupId != null && groupIds.has(account.parentGroupId)
                ? { ...account, kickType: type }
                : account,
        ),
    };
}

export function prefillTopLevelTypes(
    chart: ChartOfAccountsTemplateUploadResponse,
): ChartOfAccountsTemplateUploadResponse {
    return chart.groups
        .filter(({ parentGroupId }) => parentGroupId == null)
        .reduce((current, rootGroup) => {
            if (rootGroup.kickType != null) {
                return applyTopLevelType({
                    chart: current,
                    rowId: rootGroup.id,
                    type: rootGroup.kickType,
                });
            }
            const [first, ...rest] = subtreeAccounts({
                chart,
                rootGroupId: rootGroup.id,
            }).map(({ kickType }) => kickType);
            const isAgreed =
                first != null && rest.every((type) => type === first);
            if (isAgreed) {
                return applyTopLevelType({
                    chart: current,
                    rowId: rootGroup.id,
                    type: first,
                });
            }
            return {
                ...current,
                groups: current.groups.map((group) =>
                    group.id === rootGroup.id
                        ? { ...group, kickType: null }
                        : group,
                ),
            };
        }, chart);
}

export function buildTypeMappingRows(
    chart: ChartOfAccountsTemplateUploadResponse,
): TypeMappingRow[] {
    const childrenOf = (parentGroupId: string | null) =>
        [
            ...chart.groups
                .filter((group) => group.parentGroupId === parentGroupId)
                .map((row): TypeMappingChild => ({ row, kind: "group" })),
            ...chart.accounts
                .filter((account) => account.parentGroupId === parentGroupId)
                .map((row): TypeMappingChild => ({ row, kind: "account" })),
        ].sort((a, b) => a.row.sortOrder - b.row.sortOrder);

    const visit = (parentGroupId: string | null): TypeMappingRow[] =>
        childrenOf(parentGroupId).map(({ row, kind }) => ({
            id: row.id,
            kind,
            name: row.name,
            codeText: row.codeText,
            isTopLevel: parentGroupId == null,
            type: row.kickType,
            children: kind === "group" ? visit(row.id) : [],
        }));
    return visit(null);
}

export function toTypedChart(
    chart: ChartOfAccountsTemplateUploadResponse,
): TypedChartOfAccountsTemplateUpload | null {
    const groups: TypedChartOfAccountsTemplateUploadRow[] = [];
    const accounts: TypedChartOfAccountsTemplateUploadRow[] = [];
    for (const group of chart.groups) {
        if (group.kickType == null) {
            return null;
        }
        groups.push({ ...group, kickType: group.kickType });
    }
    for (const account of chart.accounts) {
        if (account.kickType == null) {
            return null;
        }
        accounts.push({ ...account, kickType: account.kickType });
    }
    return { groups, accounts };
}
